/**
 * SantinhoPage - Página pública do tenant (Santinho Digital)
 *
 * Renderiza o template selecionado pelo candidato com seus dados públicos.
 */

import { useEffect } from 'react';
import { Construction, Sparkles } from 'lucide-react';
import { useTenant } from '@/contexts';
import { usePageViewBeacon } from '@/hooks/usePageViewBeacon';
import { TenantPublicConfig, SantinhoTemplate } from '@/types/tenant.types';
import {
  ClassicTemplate,
  ModernTemplate,
  BoldTemplate,
  MinimalTemplate,
  CampaignTemplate,
} from '@/components/santinho';
import { BlocksTemplate } from '@/components/santinho/templates/BlocksTemplate';
import { Logo } from '@/components/Logo';

const templates: Record<SantinhoTemplate, React.ComponentType<{ tenant: TenantPublicConfig }>> = {
  classic: ClassicTemplate,
  modern: ModernTemplate,
  bold: BoldTemplate,
  minimal: MinimalTemplate,
  campaign: CampaignTemplate,
};

function shouldUseBlocksRenderer(tenant: TenantPublicConfig): boolean {
  // Tenant migrou: tem blocks customizados OU preset v2 aplicado
  const hasBlocks = !!tenant.publicPage.blocks && tenant.publicPage.blocks.length > 0;
  const hasPreset = !!tenant.publicPage.presetId;
  return hasBlocks || hasPreset;
}

export function SantinhoPage() {
  const { tenant, isLoading, error } = useTenant();

  // Fire visit beacon once per session when the public page is enabled and tenant is loaded
  usePageViewBeacon(tenant?.slug, tenant?.publicPage?.enabled === true);

  useEffect(() => {
    if (!tenant) return;

    const seo = tenant.publicPage.seo;
    const pageTitle = seo?.ogTitle || tenant.publicPage.title || tenant.name;
    const pageDesc =
      seo?.ogDescription ||
      tenant.publicPage.description ||
      `Conheça a campanha de ${tenant.name} e entre em contato pelo WhatsApp.`;
    const ogImage =
      seo?.ogImage ||
      tenant.publicPage.content.candidatePhoto ||
      tenant.publicPage.content.coverPhoto ||
      'https://campanhaflow.com.br/og-image.png';

    document.title = `${pageTitle} | CampanhaFlow`;

    const setMeta = (sel: string, val: string) => {
      document.querySelector(sel)?.setAttribute('content', val);
    };

    setMeta('meta[name="description"]', pageDesc);
    setMeta('meta[property="og:title"]', pageTitle);
    setMeta('meta[property="og:description"]', pageDesc);
    setMeta('meta[property="og:image"]', ogImage);
    setMeta('meta[name="twitter:title"]', pageTitle);
    setMeta('meta[name="twitter:description"]', pageDesc);
    setMeta('meta[name="twitter:image"]', ogImage);

    return () => {
      document.title = 'CampanhaFlow — WhatsApp para Campanhas Eleitorais';
    };
  }, [tenant]);

  if (isLoading) return <SantinhoSkeleton />;
  if (error || !tenant) return <SantinhoNotFound />;
  if (!tenant.publicPage.enabled) return <SantinhoDisabled tenant={tenant} />;

  const migratedTenant = migrateTenantData(tenant);

  // Tenant migrado para v2 (blocks + preset) usa o renderer dinamico.
  if (shouldUseBlocksRenderer(migratedTenant)) {
    return <BlocksTemplate tenant={migratedTenant} />;
  }

  const templateId = migratedTenant.publicPage.template || 'classic';
  const TemplateComponent = templates[templateId] || templates.classic;

  return <TemplateComponent tenant={migratedTenant} />;
}

function migrateTenantData(tenant: TenantPublicConfig): TenantPublicConfig {
  const content = { ...tenant.publicPage.content };
  if (content.heroImage && !content.coverPhoto) {
    content.coverPhoto = content.heroImage;
  }
  return {
    ...tenant,
    publicPage: {
      ...tenant.publicPage,
      template: tenant.publicPage.template || 'classic',
      content,
    },
  };
}

function SantinhoSkeleton() {
  return (
    <div className="min-h-screen bg-ink-50 animate-pulse">
      <div className="h-64 md:h-80 bg-gradient-to-br from-primary-200 to-accent-200" />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="h-8 bg-ink-200 rounded-xl w-1/2" />
          <div className="h-4 bg-ink-200 rounded w-3/4" />
          <div className="h-4 bg-ink-200 rounded w-2/3" />
          <div className="grid grid-cols-3 gap-3 mt-6">
            <div className="h-24 bg-ink-200 rounded-xl" />
            <div className="h-24 bg-ink-200 rounded-xl" />
            <div className="h-24 bg-ink-200 rounded-xl" />
          </div>
        </div>
      </div>
    </div>
  );
}

function SantinhoNotFound() {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-mesh-1" />
      <div className="absolute inset-0 grid-pattern mask-fade-y opacity-30" />

      <div className="relative text-center max-w-md">
        <div className="font-display text-7xl md:text-8xl font-extrabold text-gradient mb-4">
          404
        </div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-ink-900 mb-2">
          Página não encontrada
        </h1>
        <p className="text-ink-600 mb-6">
          A página que você está procurando não existe ou foi removida.
        </p>
        <a
          href="https://campanhaflow.com.br"
          className="btn-primary inline-flex"
        >
          <Sparkles className="h-4 w-4" />
          Ir para CampanhaFlow
        </a>
      </div>
    </div>
  );
}

function SantinhoDisabled({ tenant }: { tenant: TenantPublicConfig }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${tenant.theme.primaryColor}, ${tenant.theme.secondaryColor || tenant.theme.primaryColor})`,
      }}
    >
      <div className="absolute inset-0 grid-pattern opacity-10" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-blob" />
      <div
        className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-blob"
        style={{ animationDelay: '6s' }}
      />

      <div className="relative text-center text-white max-w-md">
        {tenant.theme.logoUrl ? (
          <img
            src={tenant.theme.logoUrl}
            alt={tenant.name}
            className="h-24 mx-auto mb-6 drop-shadow-2xl"
          />
        ) : (
          <div className="inline-flex h-20 w-20 items-center justify-center rounded-3xl bg-white/20 backdrop-blur-md mb-6">
            <Construction className="h-10 w-10" strokeWidth={2} />
          </div>
        )}
        <h1 className="font-display text-3xl md:text-4xl font-extrabold mb-3">{tenant.name}</h1>
        <p className="text-white/90 text-base md:text-lg">Esta página está em construção.</p>
        <p className="text-white/60 text-sm mt-2">Volte em breve para conferir as novidades.</p>

        <div className="mt-12 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20">
          <Logo size={20} />
          <span className="text-xs font-semibold text-white/80">Powered by CampanhaFlow</span>
        </div>
      </div>
    </div>
  );
}
